(() => {
  'use strict';
  const Core = window.ClarkCatalogCore;
  if (!Core) return;
  const defaultFacetKeys = ['vendor', 'type', 'material', 'color', 'orientation', 'stud', 'interior', 'grids'];
  const numberNames = ['widthMin', 'widthMax', 'heightMin', 'heightMax'];
  const parameter = name => 'cf_' + name;
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  function setup(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const labels = JSON.parse(root.querySelector('[data-catalog-labels]').textContent);
    const form = root.querySelector('[data-catalog-filters]'), grid = root.querySelector('[data-catalog-grid]');
    const status = root.querySelector('[data-catalog-status]'), pages = root.querySelector('[data-catalog-pages]');
    const sort = root.querySelector('[data-catalog-sort]'), errors = root.querySelector('[data-catalog-error]');
    const facetRoot = root.querySelector('[data-catalog-facets]');
    const controls = root.querySelector('[data-size-controls]');
    const isSearch = root.dataset.search === 'true';
    const isWindows = /\/collections\/windows\/?$/.test(root.dataset.source);
    const facetKeys = isWindows ? ['type', 'color', 'interior', 'quantity', 'vendor', 'material', 'grids', 'gridDetail'] : defaultFacetKeys;
    let products = [], bounds = {}, loaded = false, loading = false, page = 1, renderTimer;
    const pageSize = 48;
    const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: root.dataset.currency, maximumFractionDigits: value % 100 ? 2 : 0 }).format(value / 100);
    const readNumber = input => input.value.trim() === '' ? null : Number(input.value);
    const state = () => {
      const result = { inStock: false, priceMin: null, priceMax: null, facets: {} };
      numberNames.forEach(name => { result[name] = readNumber(form.elements[name]); });
      facetKeys.forEach(key => { result.facets[key] = Array.from(form.querySelectorAll('input[data-facet="' + key + '"]:checked')).map(input => input.value); });
      return result;
    };
    function restore() {
      const url = new URL(location.href);
      ['priceMin', 'priceMax', 'inStock'].forEach(name => url.searchParams.delete(parameter(name)));
      if (url.href !== location.href) history.replaceState(null, '', url);
      const params = url.searchParams;
      numberNames.forEach(name => {
        const value = params.get(parameter(name));
        form.elements[name].value = value !== null && value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0 ? value : '';
      });
      facetKeys.forEach(key => {
        const values = params.getAll(parameter(key));
        form.querySelectorAll('input[data-facet="' + key + '"]').forEach(input => { input.checked = values.includes(input.value); });
      });
      const requested = params.get('sort_by') || root.dataset.defaultSort;
      sort.value = Array.from(sort.options).some(option => option.value === requested) ? requested : (isSearch ? 'relevance' : 'manual');
      const requestedPage = Number(params.get('page') || 1);
      page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
      syncRanges();
    }
    function syncRanges() {
      for (const axis of ['width', 'height']) {
        const range = form.querySelector('[data-range="' + axis + '"]');
        const [min, max] = bounds[axis] || [0, 500];
        const low = readNumber(form.elements[axis + 'Min']), high = readNumber(form.elements[axis + 'Max']);
        const lowValue = Math.max(min, Math.min(max, low ?? min));
        const highValue = Math.max(min, Math.min(max, high ?? max));
        for (const suffix of ['Low', 'High']) { form.elements[axis + suffix].min = min; form.elements[axis + suffix].max = max; }
        form.elements[axis + 'Low'].value = lowValue;
        form.elements[axis + 'High'].value = highValue;
        form.elements[axis + 'Low'].setAttribute('aria-valuetext', lowValue + ' inches');
        form.elements[axis + 'High'].setAttribute('aria-valuetext', highValue + ' inches');
        form.elements[axis + 'Low'].style.zIndex = lowValue > max - (max - min) * .05 ? '3' : '1';
        range.style.setProperty('--low', ((lowValue - min) / (max - min) * 100) + '%');
        range.style.setProperty('--high', ((highValue - min) / (max - min) * 100) + '%');
        range.querySelector('[data-bound-low]').textContent = min + '″';
        range.querySelector('[data-bound-high]').textContent = max + '″';
        form.elements[axis + 'Min'].placeholder = min;
        form.elements[axis + 'Max'].placeholder = max;
      }
    }
    function validate() {
      for (const prefix of ['width', 'height']) {
        const low = form.elements[prefix + 'Min'], high = form.elements[prefix + 'Max'];
        const a = readNumber(low), b = readNumber(high);
        high.setCustomValidity(a !== null && b !== null && a > b ? labels.invalid : '');
      }
      return form.checkValidity();
    }
    function writeURL() {
      const url = new URL(location.href), selected = state();
      numberNames.forEach(name => { if (selected[name] === null) url.searchParams.delete(parameter(name)); else url.searchParams.set(parameter(name), selected[name]); });
      facetKeys.forEach(key => { url.searchParams.delete(parameter(key)); selected.facets[key].forEach(value => url.searchParams.append(parameter(key), value)); });
      ['priceMin', 'priceMax', 'inStock'].forEach(name => url.searchParams.delete(parameter(name)));
      url.searchParams.set('sort_by', sort.value);
      if (page > 1) url.searchParams.set('page', page); else url.searchParams.delete('page');
      history.replaceState(null, '', url);
    }
    function buildFacets() {
      facetRoot.replaceChildren();
      const labelKeys = { vendor: 'brand', type: 'type', material: 'material', color: 'color', orientation: 'orientation', stud: 'stud', interior: 'interior', grids: 'grids', quantity: 'quantityFilter', gridDetail: 'gridDetail' };
      for (const key of facetKeys) {
        const values = [...new Set(products.map(p => String(p[key] ?? '')).filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
        if (!values.length && !isWindows) continue;
        const details = element('details', 'clark-catalog__facet'); details.dataset.facetGroup = key; details.open = ['vendor', 'type', 'material'].includes(key);
        details.append(element('summary', '', labels[labelKeys[key]]));
        const options = element('div', 'clark-catalog__facet-options');
        if (!values.length) options.append(element('p', '', labels.noOptions));
        for (const value of values) {
          const label = element('label'); const input = element('input'); input.type = 'checkbox'; input.value = value; input.dataset.facet = key;
          const count = element('span'); count.dataset.facetCount = '';
          label.append(input, element('span', '', value), count); options.append(label);
        }
        details.append(options); facetRoot.append(details);
      }
    }
    function updateCounts(selected) {
      for (const key of facetKeys) {
        const counts = new Map();
        products.filter(p => Core.matches(p, selected, key)).forEach(p => counts.set(String(p[key] ?? ''), (counts.get(String(p[key] ?? '')) || 0) + 1));
        const inputs = Array.from(form.querySelectorAll('input[data-facet="' + key + '"]'));
        inputs.forEach(input => {
          const count = counts.get(input.value) || 0;
          input.disabled = !count && !input.checked;
          input.parentElement.querySelector('[data-facet-count]').textContent = '(' + count + ')';
        });
        inputs.sort((a, b) => (counts.get(b.value) || 0) - (counts.get(a.value) || 0)
          || a.value.localeCompare(b.value, undefined, { numeric: true }));
        inputs.forEach(input => input.parentElement.parentElement.append(input.parentElement));
      }
    }
    function card(p) {
      const article = element('article', 'clark-listing-card'); article.dataset.productId = p.id;
      const imageLink = element('a', 'clark-listing-card__image'); imageLink.href = p.url; imageLink.setAttribute('aria-label', p.title);
      if (p.image) { const image = element('img'); image.src = p.image; image.alt = p.title; image.width = 600; image.height = 600; image.loading = 'lazy'; imageLink.append(image); }
      if (p.quantity !== null && p.quantity !== undefined) imageLink.append(element('span', 'clark-listing-card__qty', labels.quantity.replace('[count]', p.quantity)));
      if (p.sku) imageLink.append(element('span', 'clark-listing-card__sku', p.sku));
      const info = element('div', 'clark-listing-card__info');
      const title = element('a', 'clark-listing-card__title'); title.href = p.url; title.setAttribute('aria-label', p.title);
      title.append(element('span', 'clark-listing-card__size', p.measured ? p.w + '″ × ' + p.h + '″' : p.title));
      const summary = element('span', 'clark-listing-card__summary');
      summary.append(element('span', 'clark-listing-card__brand', p.series), element('span', 'clark-listing-card__type', p.cardType));
      if (p.door && p.orientation) summary.append(element('span', 'clark-listing-card__orientation', p.orientation));
      title.append(summary);
      const prices = element('div', 'clark-listing-card__prices'); const now = element('div', 'clark-listing-card__now', money(p.price)); now.append(element('small', '', labels.now)); prices.append(now);
      if (p.retail > p.price) { const retail = element('div', 'clark-listing-card__retail'); retail.append(element('s', '', money(p.retail)), element('small', '', labels.retail)); prices.append(retail); }
      const button = element('button', 'clark-listing-card__list'); button.type = 'button'; button.dataset.productId = p.id; button.setAttribute('aria-label', labels.add.replace('[item]', p.sku || p.title));
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg'); svg.setAttribute('viewBox', '0 0 32 32'); svg.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path'); path.setAttribute('d', 'M16 28 4 16C-4 8 6-2 16 8C26-2 36 8 28 16Z'); path.setAttribute('fill', 'none'); path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-width', '2.5'); svg.append(path);
      button.append(svg, element('span', '', labels.list));
      button.addEventListener('click', async () => {
        if (button.disabled) return;
        if (!window.GRFQApp || !Array.isArray(window.GRFQConfigs?.products)) { location.href = p.url; return; }
        button.disabled = true; button.setAttribute('aria-busy', 'true'); button.querySelector('span').textContent = labels.saving;
        try {
          if (!window.GRFQConfigs.products.some(product => String(product.id) === String(p.id))) {
            const response = await fetch(p.url.split('?')[0] + '.js'); if (!response.ok) throw new Error('Product unavailable');
            window.GRFQConfigs.products.push(await response.json());
          }
          button.classList.add('rfq-btn'); await window.GRFQApp.addProduct(button, Number(p.id));
        } catch { button.querySelector('span').textContent = labels.retry; }
        finally { button.disabled = false; button.removeAttribute('aria-busy'); if (button.querySelector('span').textContent !== labels.retry) button.querySelector('span').textContent = labels.list; }
      });
      info.append(title, prices, button); article.append(imageLink, info); return article;
    }
    function render(save = true) {
      if (!loaded || !validate()) return;
      clearTimeout(renderTimer);
      const selected = state();
      const matches = Core.sorted(products.filter(p => Core.matches(p, selected)), sort.value, root.dataset.query);
      const totalPages = Math.max(1, Math.ceil(matches.length / pageSize)); page = Math.min(page, totalPages);
      const fragment = document.createDocumentFragment();
      matches.slice((page - 1) * pageSize, page * pageSize).forEach(p => fragment.append(card(p)));
      if (!matches.length) fragment.append(element('p', 'clark-catalog__empty', labels.empty));
      grid.replaceChildren(fragment); grid.setAttribute('aria-busy', 'false');
      status.textContent = labels.count.replace('[count]', matches.length);
      const missing = products.filter(p => !p.measured).length;
      if (missing && ['widthMin', 'widthMax', 'heightMin', 'heightMax'].some(name => selected[name] !== null)) status.textContent += ' ' + labels.excluded.replace('[count]', missing);
      pages.replaceChildren(); pages.className = 'clark-catalog__pages';
      const addPage = (text, target, current = false, disabled = false) => {
        const button = element('button', '', text); button.type = 'button'; button.disabled = disabled;
        if (current) { button.setAttribute('aria-current', 'page'); button.setAttribute('aria-label', labels.page.replace('[page]', target)); }
        button.addEventListener('click', () => { page = target; render(); root.querySelector('.clark-catalog__toolbar').scrollIntoView({ block: 'start', behavior: 'smooth' }); pages.querySelector('[aria-current]')?.focus({ preventScroll: true }); });
        pages.append(button);
      };
      if (totalPages > 1) {
        addPage(labels.previous, page - 1, false, page === 1);
        const numbers = [...new Set([1, page - 1, page, page + 1, totalPages])].filter(n => n > 0 && n <= totalPages).sort((a, b) => a - b);
        numbers.forEach((n, index) => { if (index && n > numbers[index - 1] + 1) pages.append(element('span', '', '…')); addPage(String(n), n, n === page); });
        addPage(labels.next, page + 1, false, page === totalPages);
      }
      updateCounts(selected); if (save) writeURL();
      root.dataset.loadedCount = products.length; root.dataset.matchCount = matches.length;
    }
    function schedule() { page = 1; clearTimeout(renderTimer); renderTimer = setTimeout(() => render(), 100); }
    form.addEventListener('submit', event => { event.preventDefault(); if (validate()) render(); else form.reportValidity(); });
    form.addEventListener('input', event => {
      const input = event.target;
      if (input.type === 'range') {
        const axis = input.name.startsWith('width') ? 'width' : 'height'; const low = input.name.endsWith('Low');
        const other = readNumber(form.elements[axis + (low ? 'Max' : 'Min')]);
        const value = Number(input.value); form.elements[axis + (low ? 'Min' : 'Max')].value = other === null ? value : (low ? Math.min(value, other) : Math.max(value, other));
      }
      syncRanges(); schedule();
    });
    form.addEventListener('change', event => { if (event.target.type !== 'range') { if (!validate()) form.reportValidity(); schedule(); } });
    form.addEventListener('reset', () => { setTimeout(() => { form.querySelectorAll('input').forEach(input => input.setCustomValidity('')); syncRanges(); page = 1; render(); }, 0); });
    root.querySelector('[data-reset-sizes]').addEventListener('click', () => { numberNames.filter(name => /width|height/.test(name)).forEach(name => { form.elements[name].value = ''; form.elements[name].setCustomValidity(''); }); syncRanges(); page = 1; render(); });
    sort.addEventListener('change', () => { page = 1; render(); });
    window.addEventListener('popstate', () => { if (loaded) { restore(); render(false); } });
    const filters = root.querySelector('.clark-catalog__filters');
    if (matchMedia('(max-width:749px)').matches) filters.open = false;
    root.querySelector('[data-dimension-note]').textContent = isWindows ? labels.basisWindows : (root.dataset.doorBasis === 'common' ? labels.basisCommon : labels.basisOverall);
    async function load() {
      if (loading) return; loading = true; loaded = false; errors.hidden = true; controls.disabled = true; status.textContent = labels.loading; grid.setAttribute('aria-busy', 'true');
      try {
        const source = new URL(root.dataset.source, location.origin); source.searchParams.set('view', 'clark-catalog-data');
        // Relevance order can change between Shopify search pages, causing omissions.
        // Retrieve in a stable price order, then rank matching products locally.
        source.searchParams.set('sort_by', isSearch ? 'price-ascending' : 'manual');
        if (isSearch) { source.searchParams.set('q', root.dataset.query); source.searchParams.set('type', 'product'); source.searchParams.set('options[prefix]', 'last'); }
        const get = async pageNumber => {
          const url = new URL(source); url.searchParams.set('page', pageNumber);
          const response = await fetch(url, { cache: 'no-store', headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Catalog request failed');
          const data = await response.json();
          if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || data.page !== pageNumber) throw new Error('Invalid catalog data');
          return data;
        };
        const first = await get(1); const batches = [first];
        for (let next = 2; next <= first.pages; next += 3) {
          const group = await Promise.all(Array.from({ length: Math.min(3, first.pages - next + 1) }, (_, offset) => get(next + offset))); batches.push(...group);
        }
        const all = batches.flatMap(batch => batch.products);
        if (all.length !== first.total || new Set(all.map(p => String(p.id))).size !== all.length) throw new Error('Incomplete or changing catalog. Retry.');
        products = all.map((p, index) => Core.prepare(p, index, root.dataset.doorBasis));
        for (const [axis, key] of [['width', 'w'], ['height', 'h']]) {
          const values = products.filter(p => p.measured).map(p => p[key]);
          const min = values.length ? Math.floor(Math.min(...values) * 8) / 8 : 0;
          const max = values.length ? Math.ceil(Math.max(...values) * 8) / 8 : 100;
          bounds[axis] = [min, Math.max(min + 1, max)];
        }
        root.querySelector('[data-dimension-note]').textContent = isWindows ? labels.basisWindows : (root.dataset.doorBasis === 'common' ? labels.basisCommon : labels.basisOverall);
        buildFacets(); restore(); loaded = true; controls.disabled = false; render(false);
      } catch { errors.hidden = false; status.textContent = ''; grid.setAttribute('aria-busy', 'false'); }
      finally { loading = false; }
    }
    root.querySelector('[data-catalog-retry]').addEventListener('click', load);
    load();
  }
  document.querySelectorAll('[data-clark-catalog]').forEach(setup);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-clark-catalog]').forEach(setup));
})();
